import {t, Trans} from '@lingui/macro';
import React, {FC, useMemo, useState} from 'react';
import styled from 'styled-components';

import {useInitialEffect} from '../../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {
  AsyncHandler,
  Handler,
  HandlerOf,
  PredicateOf,
} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {TextBox} from '../../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {PaletteColorsEnum} from '../../../../../libs/shared/ui/src/styles/paletteModel';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useLocalize} from '../../../../../srcCommon/internationalization/internationalizationContext';
import {reportToBugsnag} from '../../../../../srcCommon/services/bugsnag/bugsnag';
import {FrameTypesEnum} from '../../../../models/api/apiTypes';
import {
  IntegrationTypeCategoriesEnum,
  IntegrationTypeModel,
} from '../../../../models/api/integrationTypesModel';
import {ResourceMap} from '../../../../store/appTypes';
import {SidePanelFrame} from '../../../../store/stateless/sidePanelFrameSelectors';
import {DropdownEmpty} from '../../../common/dropdowns/dropdownEmpty';
import {SidePanelHeader} from '../sidePanelHeader';
import {EntryPointFrameCard} from './frameCard/entryPointFrameCard';
import {IntegrationFrameCard} from './frameCard/integrationFrameCard';
import {IntegrationFrameCardCategory} from './frameCard/integrationFrameCardCategory';
import {IntegrationTypeFrameCard} from './frameCard/integrationTypeFrameCard';
import {NativeFrameCard} from './frameCard/nativeFrameCard';
import {PluginFrameCard} from './frameCard/pluginFrameCard';
import {SidePanelManagerSection} from './sidePanelManagerSection';
import {SidePanelManagerSectionLink} from './sidePanelManagerSectionLink';

/*
 * Props.
 */

interface SidePanelManagerProps {
  host: string;
  integrationTypes: ResourceMap<IntegrationTypeModel>;
  availableIntegrationTypes: ReadonlyArray<IntegrationTypeModel>;
  frames: ReadonlyArray<SidePanelFrame>;
  isFramePinned: PredicateOf<SidePanelFrame>;
  onTogglePinFrameClick: HandlerOf<SidePanelFrame>;
  onIntegrationTypeClick: HandlerOf<IntegrationTypeModel>;
  onOpenAppDirectoryClick: Handler;
  fetchIntegrationTypesAsync: AsyncHandler;
}

/*
 * Styles.
 */

const StyledContentDiv = styled.div`
  flex: 1;

  display: flex;
  flex-direction: column;

  overflow: scroll;
`;

const StyledCalendarTitleDiv = styled.div`
  display: flex;
  align-items: center;
`;

/*
 * Component.
 */

export const SidePanelManager: FC<SidePanelManagerProps> = (props) => {
  const {fetchIntegrationTypesAsync} = props;
  const [searchQuery, setSearchQuery] = useState('');
  const localize = useLocalize();

  useInitialEffect(() => {
    fetchIntegrationTypesAsync();
  });

  return (
    <>
      <SidePanelHeader title={<Trans id="sidepanel-manager-title">Integrations</Trans>}>
        <TextBox placeholder={localize(t('dropdown-search-placeholder')`Search`)} onChange={setSearchQuery} />
      </SidePanelHeader>
      <SidePanelContent {...props} searchQuery={searchQuery} />
    </>
  );
};

const SidePanelContent: FC<SidePanelManagerProps & {searchQuery: string}> = (props) => {
  const {integrationTypes, availableIntegrationTypes, frames, searchQuery} = props;

  // Filter the frames with the current search query.
  const filteredFrames = useMemo(() => {
    const normalizedSearchQuery = searchQuery.toLowerCase().trim();
    return frames.filter((f) => isFrameMatch(integrationTypes, normalizedSearchQuery, f));
  }, [integrationTypes, frames, searchQuery]);

  // Filter the integration types with the current search query.
  const filteredIntegrationTypes = useMemo(() => {
    const normalizedSearchQuery = searchQuery.toLowerCase().trim();
    return availableIntegrationTypes.filter((i) => isIntegrationTypeMatch(normalizedSearchQuery, i));
  }, [availableIntegrationTypes, searchQuery]);

  // Empty state.
  if (filteredFrames.length === 0 && filteredIntegrationTypes.length === 0) {
    return <DropdownEmpty />;
  }

  return (
    <StyledContentDiv>
      {maybeRenderFrames(props, filteredFrames)}
      {maybeRenderIntegrationTypes(props, filteredIntegrationTypes)}
    </StyledContentDiv>
  );
};

/*
 * Frames.
 */

function maybeRenderFrames(props: SidePanelManagerProps, filteredFrames: ReadonlyArray<SidePanelFrame>) {
  if (filteredFrames.length === 0) {
    return null;
  }

  return (
    <SidePanelManagerSection>{filteredFrames.map((f) => renderFrame(props, f))}</SidePanelManagerSection>
  );
}

function renderFrame(props: SidePanelManagerProps, frame: SidePanelFrame) {
  const {integrationTypes, onTogglePinFrameClick} = props;
  const {frameId} = frame;
  const isFramePinned = props.isFramePinned(frame);

  switch (frame.frameType) {
    case FrameTypesEnum.INTEGRATION:
      return (
        <IntegrationFrameCard
          key={frameId}
          integration={frame}
          integrationType={integrationTypes[frame.type]}
          isPinned={isFramePinned}
          onClick={() => onTogglePinFrameClick(frame)}
        />
      );

    case FrameTypesEnum.PLUGIN:
      return (
        <PluginFrameCard
          key={frameId}
          plugin={frame}
          isPinned={isFramePinned}
          onClick={() => onTogglePinFrameClick(frame)}
        />
      );

    case FrameTypesEnum.ENTRY_POINT:
      return (
        <EntryPointFrameCard
          key={frameId}
          entryPoint={frame}
          isPinned={isFramePinned}
          onClick={() => onTogglePinFrameClick(frame)}
        />
      );

    case FrameTypesEnum.CALENDAR:
      return (
        <NativeFrameCard
          key={frameId}
          name={
            <StyledCalendarTitleDiv>
              <Trans id="sidepanel-calendar-title">Calendar</Trans>
            </StyledCalendarTitleDiv>
          }
          category={<IntegrationFrameCardCategory category={IntegrationTypeCategoriesEnum.ESSENTIALS} />}
          iconName="calendar"
          color={PaletteColorsEnum.RED}
          isPinned={isFramePinned}
          onClick={() => onTogglePinFrameClick(frame)}
        />
      );
    case FrameTypesEnum.CUSTOMER_VIEW:
      return (
        <NativeFrameCard
          key={frameId}
          name={<Trans id="sidepanel-customerview-title">Contact details</Trans>}
          category={<IntegrationFrameCardCategory category={IntegrationTypeCategoriesEnum.ESSENTIALS} />}
          iconName="contacts"
          color={frame.color}
          isPinned={isFramePinned}
          onClick={() => onTogglePinFrameClick(frame)}
        />
      );
    case FrameTypesEnum.START_GUIDE:
      return (
        <UserThemeConsumer>
          {(theme) => (
            <NativeFrameCard
              key={frameId}
              name={<Trans id="sidepanel-helpandtips-title">Help and Tips</Trans>}
              category={<Trans id="sidepanel-helpandtips-category">Essentials</Trans>}
              iconName="questionMark"
              color={theme.greys.shade90}
              isPinned={isFramePinned}
              onClick={() => onTogglePinFrameClick(frame)}
            />
          )}
        </UserThemeConsumer>
      );
    case FrameTypesEnum.KEYBOARD_HELP:
      return (
        <NativeFrameCard
          key={frameId}
          name={<Trans id="sidepanel-keyboardhelp-title">Keyboard Shortcuts</Trans>}
          category={<IntegrationFrameCardCategory category={IntegrationTypeCategoriesEnum.ESSENTIALS} />}
          iconName="keyboard"
          color={PaletteColorsEnum.GREY}
          isPinned={isFramePinned}
          onClick={() => onTogglePinFrameClick(frame)}
        />
      );

    default:
      reportToBugsnag(new Error('Unable to render frame in sidePanelManager.'), {
        metadata: {
          frameType: frame.frameType,
        },
      });
      return null;
  }
}

/*
 * Integration types.
 */

function maybeRenderIntegrationTypes(
  props: SidePanelManagerProps,
  filteredIntegrationTypes: ReadonlyArray<IntegrationTypeModel>,
) {
  if (filteredIntegrationTypes.length === 0) {
    return null;
  }

  const appDirectoryLink = (
    <SidePanelManagerSectionLink onClick={props.onOpenAppDirectoryClick}>
      <Trans id="sidepanel-manager-appdirectory">App directory</Trans>
    </SidePanelManagerSectionLink>
  );

  return (
    <SidePanelManagerSection
      title={<Trans id="sidepanel-manager-addnew">Add new</Trans>}
      link={appDirectoryLink}
    >
      {filteredIntegrationTypes.map((i) => renderIntegrationType(props, i))}
    </SidePanelManagerSection>
  );
}

function renderIntegrationType(props: SidePanelManagerProps, integrationType: IntegrationTypeModel) {
  return (
    <IntegrationTypeFrameCard
      host={props.host}
      key={integrationType.id}
      integrationType={integrationType}
      onClick={() => props.onIntegrationTypeClick(integrationType)}
    />
  );
}

/*
 * Helpers.
 */

function isFrameMatch(
  integrationTypes: ResourceMap<IntegrationTypeModel>,
  searchQuery: string,
  frame: SidePanelFrame,
) {
  if (!searchQuery) {
    return true;
  }

  switch (frame.frameType) {
    case FrameTypesEnum.ENTRY_POINT:
    case FrameTypesEnum.INTEGRATION: {
      // Check the integration name first.
      if (frame.name.toLowerCase().includes(searchQuery)) {
        return true;
      }

      // Then check the integration type.
      const integrationType = integrationTypes[frame.type];
      return Boolean(integrationType && isIntegrationTypeMatch(searchQuery, integrationType));
    }

    case FrameTypesEnum.PLUGIN:
      return frame.name.toLowerCase().includes(searchQuery);

    default:
      return false;
  }
}

function isIntegrationTypeMatch(searchQuery: string, integrationType: IntegrationTypeModel) {
  if (integrationType.name.toLowerCase().includes(searchQuery)) {
    return true;
  }

  return integrationType.searchKeywords.some((k) => k.toLowerCase().includes(searchQuery));
}
